import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/layoutChrome";
import { ModuleFeatureCard } from "@/components/HubModuleCard";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { resolveModuleNavIcon } from "@/modules/moduleNavIcons";
import {
  DESIGNER_DATA_ADMIN_CARDS,
  DESIGNER_DATA_ADMIN_SECTION,
  resolveDesignerToolCardPath,
} from "@/pages/disenador/designerToolCards";
import { Database, LayoutDashboard } from "lucide-react";
import SatCycleMonitorAccordion from "@/components/disenador/SatCycleMonitorAccordion";
import SatRobotHubAccordion from "@/components/disenador/SatRobotHubAccordion";
import DesignerBuyerUxPreferences from "@/components/disenador/DesignerBuyerUxPreferences";

function DesignerToolsCardGrid({ cards, onOpen }) {
  if (!cards?.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((card) => {
        const Icon = resolveModuleNavIcon(card.iconKey, LayoutDashboard);
        const pending = card.status === "partial" || card.pending;
        return (
          <ModuleFeatureCard
            key={`${card.path}${card.hash || ""}-${card.testid}`}
            label={card.label}
            description={card.description}
            icon={Icon}
            hasModuleAccess={!pending}
            accessLoading={false}
            pending={pending}
            testId={card.testid}
            cardStatus={card.status}
            onEnter={() => {
              if (pending) return;
              onOpen(card);
            }}
          />
        );
      })}
    </div>
  );
}

export default function DesignerToolsHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  const dataAdminCards = useMemo(() => DESIGNER_DATA_ADMIN_CARDS, []);

  if (user && !isPlatformDesigner) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <p className="text-sm text-slate-600">No tiene acceso a herramientas del diseñador.</p>
      </div>
    );
  }

  const openTool = (card) => {
    navigate(resolveDesignerToolCardPath(card));
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Herramientas Técnicas"
        subtitle={
          <>
            Diagnóstico, respaldos y mantenimiento de soporte. La operación diaria del Visor SAT está en{" "}
            <Link to="/" className="text-[#0F2D5C] font-semibold underline">
              Panel General → Visor SAT
            </Link>
            .
          </>
        }
        breadcrumb={["Módulo Maestro Diseñador", "Herramientas Técnicas"]}
      />
      <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="designer-tools-hub">
        <DesignerBuyerUxPreferences />

        <p
          className="text-xs text-slate-600 m-0 leading-relaxed rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
          data-testid="designer-tools-support-note"
        >
          Controles SAT avanzados (robot y monitor) están plegados abajo — solo para soporte BNT. No sustituyen al
          módulo operativo Visor SAT.
        </p>

        <section
          aria-labelledby="designer-data-admin-heading"
          data-testid="designer-tools-data-admin-section"
          className="space-y-4"
        >
          <div className="flex items-start gap-3">
            <Database className="w-5 h-5 shrink-0 text-[#0F2D5C] mt-0.5" aria-hidden />
            <div>
              <h2
                id="designer-data-admin-heading"
                className="text-base font-semibold text-[#0F2D5C] m-0"
              >
                {DESIGNER_DATA_ADMIN_SECTION.title}
              </h2>
              <p className="text-sm text-slate-600 mt-1 m-0 leading-relaxed max-w-3xl">
                {DESIGNER_DATA_ADMIN_SECTION.intro}
              </p>
            </div>
          </div>
          <DesignerToolsCardGrid cards={dataAdminCards} onOpen={openTool} />
        </section>

        <section aria-label="Soporte SAT avanzado (plegado)" className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-700 m-0">Soporte SAT avanzado</h2>
          <SatRobotHubAccordion />
          <SatCycleMonitorAccordion />
        </section>
      </div>
    </div>
  );
}
